/**
 * Centralized Search Cache Utility
 * Provides sessionStorage-backed caching for large dataset searches
 * with automatic TTL expiration and multi-field filtering
 * 
 * Now with Typesense integration for faster searches!
 */

import React, { useState, useCallback } from 'react';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from './firebase';
import { collectionPath } from './firestoreCollections';
import { search as typesenseSearch, isTypesenseAvailable } from './typesense';

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const DEFAULT_LIMIT = 5000;

/**
 * Cache key generator
 */
const getCacheKey = (entityType, userRole = 'default') => {
  return `searchCache_${entityType}_${userRole}`;
};

/**
 * Get cached data if valid (not expired)
 */
const getFromCache = (cacheKey) => {
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;

    // Return data if cache is still valid
    if (age < CACHE_TTL) {
      console.log(`[SearchCache] Cache hit for ${cacheKey} (age: ${Math.round(age / 1000)}s)`);
      return data;
    }

    // Cache expired, remove it
    console.log(`[SearchCache] Cache expired for ${cacheKey}`);
    sessionStorage.removeItem(cacheKey);
    return null;
  } catch (error) {
    console.error('[SearchCache] Error reading cache:', error);
    return null;
  }
};

/**
 * Store data in cache with timestamp
 */
const setCache = (cacheKey, data) => {
  try {
    const cacheEntry = {
      data,
      timestamp: Date.now()
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    console.log(`[SearchCache] Cached ${data.length} records for ${cacheKey}`);
  } catch (error) {
    console.error('[SearchCache] Error writing cache:', error);
    // If quota exceeded, clear old caches and retry
    if (error.name === 'QuotaExceededError') {
      clearAllCaches();
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));
      } catch (retryError) {
        console.error('[SearchCache] Still failed after clearing caches');
      }
    }
  }
};

/**
 * Clear all search caches
 */
export const clearAllCaches = () => {
  const keys = Object.keys(sessionStorage);
  keys.forEach(key => {
    if (key.startsWith('searchCache_')) {
      sessionStorage.removeItem(key);
    }
  });
  console.log('[SearchCache] Cleared all search caches');
};

/**
 * Clear specific entity cache
 */
export const clearEntityCache = (entityType, userRole = 'default') => {
  const cacheKey = getCacheKey(entityType, userRole);
  sessionStorage.removeItem(cacheKey);
  console.log(`[SearchCache] Cleared cache for ${cacheKey}`);
};

/**
 * Load customers with caching
 */
export const loadcustomers = async (userRole = 'default', roleFilter = null) => {
  const cacheKey = getCacheKey('customers', userRole);
  
  // Check cache first
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  // Fetch from Firestore
  console.log('[SearchCache] Loading customers from Firestore...');
  try {
    const q = query(
      collection(db, collectionPath('customers')),
      orderBy('customerName', 'asc'),
      limit(DEFAULT_LIMIT)
    );

    const snapshot = await getDocs(q);
    let data = snapshot.docs.map(doc => {
      const docData = doc.data();
      return {
        id: doc.id,
        customerName: docData.customerName || '',
        customerEmail: docData.customerEmail || '',
        apparelMagicId: docData.apparelMagicId || '',
        customerStatus: docData.customerStatus || '',
        customerPhone: docData.customerPhone || '',
        customerCity: docData.customerCity || '',
        customerState: docData.customerState || '',
        salesReps: Array.isArray(docData.salesReps) ? docData.salesReps : [],
        // Denormalized search field (if exists)
        searchTerms: docData.searchTerms || buildSearchTerms(docData, 'customer')
      };
    });

    // Apply role-based filtering if provided
    if (roleFilter && typeof roleFilter === 'function') {
      data = data.filter(roleFilter);
    }

    // Cache the data
    setCache(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error('[SearchCache] Error loading customers:', error);
    return [];
  }
};

/**
 * Load Opportunities with caching
 */
export const loadOpportunities = async (userRole = 'default', roleFilter = null) => {
  const cacheKey = getCacheKey('opportunities', userRole);
  
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  console.log('[SearchCache] Loading opportunities from Firestore...');
  try {
    const q = query(
      collection(db, collectionPath('opportunities')),
      limit(DEFAULT_LIMIT)
    );

    const snapshot = await getDocs(q);
    let data = snapshot.docs.map(doc => {
      const docData = doc.data();
      return {
        id: doc.id,
        oppNum: docData.oppNum || docData.opportunityNumber || '',
        opportunityNumber: docData.opportunityNumber || docData.oppNum || '',
        opportunityNumberLabel: docData.opportunityNumberLabel || doc.id || '',
        description: docData.description || '',
        customerName: docData.customerName || '',
        stage: docData.stage || '',
        value: docData.value || 0,
        salesRepNames: docData.salesRepNames || [],
        searchTerms: docData.searchTerms || buildSearchTerms({
          oppNum: docData.oppNum || docData.opportunityNumber,
          opportunityNumberLabel: docData.opportunityNumberLabel || doc.id,
          customerName: docData.customerName,
          description: docData.description
        }, 'opportunity')
      };
    });

    if (roleFilter && typeof roleFilter === 'function') {
      data = data.filter(roleFilter);
    }

    setCache(cacheKey, data);
    return data;
  } catch (error) {
    console.error('[SearchCache] Error loading opportunities:', error);
    return [];
  }
};

/**
 * Load Orders with caching
 */
export const loadOrders = async (userRole = 'default', roleFilter = null) => {
  const cacheKey = getCacheKey('orders', userRole);
  
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  console.log('[SearchCache] Loading orders from Firestore...');
  try {
    const q = query(
      collection(db, collectionPath('orders')),
      orderBy('creation_time', 'desc'),
      limit(DEFAULT_LIMIT)
    );

    const snapshot = await getDocs(q);
    let data = snapshot.docs.map(doc => {
      const docData = doc.data();
      
      // Only cache essential fields to avoid sessionStorage quota
      // Order number IS the document ID in Firestore
      return {
        id: doc.id,  // This IS the order number!
        orderNumber: doc.id,  // Use document ID as order number
        order_number: doc.id,
        order_id: doc.id,
        customer_id: docData.customer_id || '',
        customer_name: docData.customer_name || docData.name || '',
        amount: docData.amount || 0,
        creation_time: docData.creation_time || '',
        date_internal: docData.date_internal || '',
        status: docData.status || '',
        searchTerms: buildSearchTerms({
          id: doc.id,
          customer_name: docData.customer_name || docData.name || '',
          customer_id: docData.customer_id || ''
        }, 'order')
      };
    });
    
    // Debug: Log first order to verify
    if (data.length > 0) {
      console.log('[SearchCache] Sample order:', data[0]);
    }

    if (roleFilter && typeof roleFilter === 'function') {
      data = data.filter(roleFilter);
    }

    setCache(cacheKey, data);
    return data;
  } catch (error) {
    console.error('[SearchCache] Error loading orders:', error);
    return [];
  }
};

/**
 * Search Orders with Typesense (primary) or Firestore fallback
 * 
 * Strategy:
 * 1. Try Typesense first (instant search across 30K+ orders)
 * 2. Fallback to cache + Firestore if Typesense unavailable
 * 
 * Benefits:
 * - 10-20x faster with Typesense (<100ms for 30K orders)
 * - Typo tolerance ("14432" matches "14422")
 * - Multi-field search
 * - Graceful degradation to Firestore
 * 
 * @param {string} searchTerm - Search query (minimum 2 characters)
 * @param {string} userRole - User role for filtering
 * @returns {Promise<Array>} - Matching orders
 */
export const searchOrders = async (searchTerm, userRole = 'default') => {
  const search = searchTerm.trim();
  if (search.length < 2) return [];

  try {
    // Try Typesense first
    const typesenseAvailable = await isTypesenseAvailable();
    
    if (typesenseAvailable) {
      console.log(`[SearchCache] Searching orders via Typesense: "${search}"`);
      
      const response = await typesenseSearch('orders', {
        q: search,
        query_by: 'order_number,customer_name,customer_id',
        per_page: 100,
        sort_by: 'creation_time:desc'
      });
      
      const results = response.hits.map(hit => ({
        id: hit.document.id,
        orderNumber: hit.document.order_number || hit.document.id,
        order_number: hit.document.order_number || hit.document.id,
        customer_name: hit.document.customer_name || '',
        customer_id: hit.document.customer_id || '',
        amount: hit.document.amount || 0,
        creation_time: hit.document.creation_time,
        date_internal: hit.document.date_internal
      }));
      
      console.log(`[SearchCache] Typesense found ${results.length} orders`);
      return results;
    }
  } catch (error) {
    console.warn('[SearchCache] Typesense search failed, using Firestore:', error);
  }

  // Fallback to Firestore with cache
  console.log(`[SearchCache] Searching orders via Firestore: "${search}"`);
  const cacheKey = getCacheKey('orders', userRole);
  const cached = getFromCache(cacheKey);
  
  if (cached) {
    return filterEntities(cached, searchTerm, [
      'id', 'orderNumber', 'order_number', 'customer_name', 'customer_id'
    ]);
  }

  const allOrders = await loadOrders(userRole);
  return filterEntities(allOrders, searchTerm, [
    'id', 'orderNumber', 'order_number', 'customer_name', 'customer_id'
  ]);
};

/**
 * Search customers with Typesense (primary) or Firestore fallback
 * 
 * @param {string} searchTerm - Search query
 * @param {string} userRole - User role for filtering
 * @returns {Promise<Array>} - Matching customers
 */
export const searchcustomers = async (searchTerm, userRole = 'default') => {
  const search = searchTerm.trim();
  if (search.length < 2) return [];

  try {
    const typesenseAvailable = await isTypesenseAvailable();
    
    if (typesenseAvailable) {
      console.log(`[SearchCache] Searching customers via Typesense: "${search}"`);
      
      const response = await typesenseSearch('customers', {
        q: search,
        query_by: 'customerName,customerEmail,apparelMagicId,city,state',
        per_page: 50,
        sort_by: 'customerName:asc'
      });
      
      return response.hits.map(hit => ({
        id: hit.document.id,
        customerName: hit.document.customerName || '',
        customerEmail: hit.document.customerEmail || '',
        apparelMagicId: hit.document.apparelMagicId || '',
        customerStatus: hit.document.customerStatus || '',
        city: hit.document.city || '',
        state: hit.document.state || ''
      }));
    }
  } catch (error) {
    console.warn('[SearchCache] Typesense search failed, using Firestore:', error);
  }

  // Firestore fallback
  const allcustomers = await loadcustomers(userRole);
  return filterEntities(allcustomers, searchTerm, [
    'customerName', 'customerEmail', 'apparelMagicId', 'customerCity', 'customerState'
  ]);
};

/**
 * Search Opportunities with Typesense (primary) or Firestore fallback
 * 
 * @param {string} searchTerm - Search query
 * @param {string} userRole - User role for filtering
 * @returns {Promise<Array>} - Matching opportunities
 */
export const searchOpportunities = async (searchTerm, userRole = 'default') => {
  const search = searchTerm.trim();
  if (search.length < 2) return [];

  try {
    const typesenseAvailable = await isTypesenseAvailable();
    
    if (typesenseAvailable) {
      console.log(`[SearchCache] Searching opportunities via Typesense: "${search}"`);
      
      const response = await typesenseSearch('opportunities', {
        q: search,
        query_by: 'title,customerName',
        per_page: 50
      });
      
      return response.hits.map(hit => ({
        id: hit.document.id,
        title: hit.document.title || '',
        customerName: hit.document.customerName || '',
        stage: hit.document.stage || '',
        value: hit.document.value || 0
      }));
    }
  } catch (error) {
    console.warn('[SearchCache] Typesense search failed, using Firestore:', error);
  }

  // Firestore fallback
  const allOpps = await loadOpportunities(userRole);
  return filterEntities(allOpps, searchTerm, [
    'title', 'oppNum', 'customerName', 'description'
  ]);
};

/**
 * Search Contacts with Typesense (primary) or Firestore fallback
 * 
 * @param {string} searchTerm - Search query
 * @param {string} userRole - User role for filtering
 * @returns {Promise<Array>} - Matching contacts
 */
export const searchContacts = async (searchTerm, userRole = 'default') => {
  const search = searchTerm.trim();
  if (search.length < 2) return [];

  try {
    const typesenseAvailable = await isTypesenseAvailable();
    
    if (typesenseAvailable) {
      console.log(`[SearchCache] Searching contacts via Typesense: "${search}"`);
      
      const response = await typesenseSearch('contacts', {
        q: search,
        query_by: 'firstName,lastName,email,customerName',
        per_page: 50
      });
      
      return response.hits.map(hit => ({
        id: hit.document.id,
        firstName: hit.document.firstName || '',
        lastName: hit.document.lastName || '',
        email: hit.document.email || '',
        customerName: hit.document.customerName || '',
        customerId: hit.document.customerId || ''
      }));
    }
  } catch (error) {
    console.warn('[SearchCache] Typesense search failed, using Firestore:', error);
  }

  // Firestore fallback
  const allContacts = await loadContacts(userRole);
  return filterEntities(allContacts, searchTerm, [
    'firstName', 'lastName', 'email', 'customerName'
  ]);
};

/**
 * Load Contacts with caching
 * NOTE: Contacts are embedded in customer.contacts arrays, not a separate collection
 */
export const loadContacts = async (userRole = 'default', roleFilter = null) => {
  const cacheKey = getCacheKey('contacts', userRole);
  
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  console.log('[SearchCache] Loading contacts from standalone collection and customer.contacts arrays...');
  try {
    // 1. Load from standalone contacts collection (created via OppModal / Contacts page)
    const standaloneSnap = await getDocs(
      query(collection(db, collectionPath('contacts')), limit(DEFAULT_LIMIT))
    );

    const allContacts = [];
    // Track emails+customerId combos already added to avoid duplicates from embedded arrays
    const seenKeys = new Set();

    standaloneSnap.docs.forEach(doc => {
      const c = doc.data();
      const key = `${c.customerId || ''}_${(c.email || '').toLowerCase()}`;
      seenKeys.add(key);
      allContacts.push({
        id: doc.id,
        customerId: c.customerId || c.contactCustomerId || '',
        customerName: c.customerName || '',
        firstName: c.firstName || '',
        lastName: c.lastName || '',
        email: c.email || '',
        phone: c.phone || '',
        title: c.contactPosition || c.title || '',
        searchTerms: buildSearchTerms({
          firstName: c.firstName || '',
          lastName: c.lastName || '',
          email: c.email || '',
          customerName: c.customerName || ''
        }, 'contact')
      });
    });

    // 2. Load embedded contacts from customer documents as fallback / supplement
    const customersSnap = await getDocs(
      query(collection(db, collectionPath('customers')), limit(DEFAULT_LIMIT))
    );

    customersSnap.docs.forEach(doc => {
      const customerData = doc.data();
      const customerId = doc.id;
      const customerName = customerData.customerName || customerData.name || '';
      const embeddedContacts = customerData.contacts || [];

      embeddedContacts.forEach((contact, index) => {
        const email = (contact.email || contact.contactEmail || '').toLowerCase();
        const key = `${customerId}_${email}`;
        // Skip if already loaded from standalone collection
        if (seenKeys.has(key)) return;
        seenKeys.add(key);

        allContacts.push({
          id: `${customerId}_${index}`,
          customerId,
          customerName,
          firstName: contact.firstName || contact.first_name || '',
          lastName: contact.lastName || contact.last_name || '',
          email: contact.email || contact.contactEmail || '',
          phone: contact.phone || contact.contactPhone || '',
          title: contact.title || contact.contactTitle || '',
          contactIndex: index,
          searchTerms: buildSearchTerms({
            firstName: contact.firstName || contact.first_name || '',
            lastName: contact.lastName || contact.last_name || '',
            email: contact.email || contact.contactEmail || '',
            customerName
          }, 'contact')
        });
      });
    });

    // Apply role filter if provided
    let data = allContacts;
    if (roleFilter && typeof roleFilter === 'function') {
      data = data.filter(roleFilter);
    }

    setCache(cacheKey, data);
    console.log(`[SearchCache] Extracted ${data.length} contacts from ${customersSnap.size} customers`);
    return data;
  } catch (error) {
    console.error('[SearchCache] Error loading contacts:', error);
    return [];
  }
};

/**
 * Build denormalized search terms from entity data
 * This is a fallback if searchTerms field doesn't exist
 */
const buildSearchTerms = (docData, entityType) => {
  const terms = [];
  
  switch (entityType) {
    case 'customer':
      if (docData.customerName) terms.push(docData.customerName.toLowerCase());
      if (docData.customerEmail) terms.push(docData.customerEmail.toLowerCase());
      if (docData.apparelMagicId) terms.push(docData.apparelMagicId.toLowerCase());
      if (docData.customerPhone) terms.push(docData.customerPhone.replace(/\D/g, ''));
      break;
      
    case 'opportunity':
      if (docData.description) terms.push(docData.description.toLowerCase());
      if (docData.oppNum) terms.push(docData.oppNum.toString());
      if (docData.customerName) terms.push(docData.customerName.toLowerCase());
      break;
      
    case 'order':
      if (docData.order_number) terms.push(docData.order_number.toLowerCase());
      if (docData.customer_id) terms.push(docData.customer_id.toLowerCase());
      if (docData.customer_name) terms.push(docData.customer_name.toLowerCase());
      break;
      
    case 'contact':
      if (docData.firstName) terms.push(docData.firstName.toLowerCase());
      if (docData.lastName) terms.push(docData.lastName.toLowerCase());
      if (docData.email) terms.push(docData.email.toLowerCase());
      if (docData.phone) terms.push(docData.phone.replace(/\D/g, ''));
      if (docData.customerName) terms.push(docData.customerName.toLowerCase());
      break;
  }
  
  return terms.join('|');
};

/**
 * Client-side filter function
 * Searches across all fields using denormalized searchTerms or individual fields
 */
export const filterEntities = (entities, searchTerm, fields) => {
  if (!searchTerm || !searchTerm.trim()) return entities;
  
  const searchLower = searchTerm.toString().toLowerCase().trim();
  
  return entities.filter(entity => {
    // Priority 1: Use denormalized searchTerms field (fastest - no toLowerCase needed)
    if (entity.searchTerms) {
      return entity.searchTerms.includes(searchLower);
    }
    
    // Priority 2: Search specified fields only (faster than all fields)
    if (fields.length > 0) {
      return fields.some(field => {
        const value = entity[field];
        if (!value) return false;
        
        // Handle different types efficiently
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchLower);
        }
        if (typeof value === 'number') {
          return value.toString().includes(searchLower);
        }
        if (Array.isArray(value)) {
          return value.some(v => 
            typeof v === 'string' && v.toLowerCase().includes(searchLower)
          );
        }
        return false;
      });
    }
    
    // Priority 3: Search all string fields (slowest - avoid if possible)
    return Object.values(entity).some(value => {
      if (typeof value === 'string') {
        return value.toLowerCase().includes(searchLower);
      }
      return false;
    });
  });
};

/**
 * Hook-friendly wrapper for use in React components
 * Returns: { data, loading, error, reload }
 */
export const useSearchCache = (entityType, userRole = 'default', roleFilter = null) => {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let result;
      switch (entityType) {
        case 'customers':
          result = await loadcustomers(userRole, roleFilter);
          break;
        case 'opportunities':
          result = await loadOpportunities(userRole, roleFilter);
          break;
        case 'orders':
          result = await loadOrders(userRole, roleFilter);
          break;
        case 'contacts':
          result = await loadContacts(userRole, roleFilter);
          break;
        default:
          throw new Error(`Unknown entity type: ${entityType}`);
      }
      setData(result);
    } catch (err) {
      console.error(`[SearchCache] Error loading ${entityType}:`, err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [entityType, userRole, roleFilter]);

  return { data, loading, error, reload: load };
};

/**
 * Batch load multiple entity types
 */
export const loadMultipleEntities = async (entityTypes, userRole = 'default', roleFilters = {}) => {
  const promises = entityTypes.map(type => {
    const roleFilter = roleFilters[type] || null;
    
    switch (type) {
      case 'customers':
        return loadcustomers(userRole, roleFilter);
      case 'opportunities':
        return loadOpportunities(userRole, roleFilter);
      case 'orders':
        return loadOrders(userRole, roleFilter);
      case 'contacts':
        return loadContacts(userRole, roleFilter);
      default:
        return Promise.resolve([]);
    }
  });

  const results = await Promise.all(promises);
  
  return entityTypes.reduce((acc, type, index) => {
    acc[type] = results[index];
    return acc;
  }, {});
};

/**
 * Get cache statistics
 */
export const getCacheStats = () => {
  const stats = {
    totalKeys: 0,
    searchCacheKeys: 0,
    totalSize: 0,
    caches: []
  };

  const keys = Object.keys(sessionStorage);
  stats.totalKeys = keys.length;

  keys.forEach(key => {
    if (key.startsWith('searchCache_')) {
      stats.searchCacheKeys++;
      try {
        const value = sessionStorage.getItem(key);
        const { data, timestamp } = JSON.parse(value);
        const age = Date.now() - timestamp;
        const size = new Blob([value]).size;
        
        stats.totalSize += size;
        stats.caches.push({
          key,
          recordCount: data.length,
          age: Math.round(age / 1000),
          size: (size / 1024).toFixed(2) + ' KB',
          isValid: age < CACHE_TTL
        });
      } catch (error) {
        // Skip invalid entries
      }
    }
  });

  stats.totalSize = (stats.totalSize / 1024).toFixed(2) + ' KB';
  
  return stats;
};







